import {
  createDraftSafeSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit'
import 'agora-rtc-sdk-ng'
import { ICameraVideoTrack, IMicrophoneAudioTrack } from 'agora-rtc-sdk-ng'

import { CallBGM, CallUserRole } from 'lib/_types'
import { DefaultRootState } from 'react-redux'
import { callApi } from 'store/api/yay/call'

const AgoraRTC = window.AgoraRTC

export const NETWORK_QUALITY = {
  good: 'good',
  bad: 'bad',
}

export enum NetworkQuality {
  Good,
  Bad,
}

export const rtcClient = AgoraRTC.createClient({
  mode: 'rtc',
  codec: 'vp8',
})

export type CallUser = {
  id: string
  uid: string
  status: 'online' | 'offline'
  role: CallUserRole
  nickname: string

  muteAudio?: boolean
  muteVideo?: boolean
  // sound level
  volumeIndicatorLevel?: number
  audioVolume?: number
  // use only for current user
  isForcedMuteVideo?: boolean
  isForcedMuteAudio?: boolean

  profile_icon_thumbnail?: string
}

export type CallReaction = {
  id: number
  userId: string
  content: string
  startFrom: string
}

export const createCallUser = (user: Partial<CallUser>): CallUser => ({
  id: null,
  uid: null,
  status: 'offline',
  role: 'participant',
  nickname: null,

  ...user,
})

export type AgoraState = {
  uid: string
  users: Record<string, CallUser>
  networkQuality: NetworkQuality
  bgmSelected: CallBGM
  bgmIsPlaying: boolean
  bgmTrackId: string
  reactions: Array<CallReaction>
  rtm?: {
    callId: number | string
    token: string
    agora_uid: string
  }
}

export const agoraInitialState: AgoraState = {
  uid: null,
  networkQuality: null,
  bgmSelected: null,
  bgmIsPlaying: false,
  bgmTrackId: null,
  reactions: [],
  users: {},
}

export const agoraSlice = createSlice({
  name: 'agora',
  initialState: agoraInitialState,
  reducers: {
    setupAgora(state, action: PayloadAction<CallUser>) {
      const user = action.payload

      state.users[user.id] = user
      state.uid = user.id
    },
    setNetworkQuality(state, action: PayloadAction<NetworkQuality>) {
      state.networkQuality = action.payload
    },
    setCallUser(
      state,
      action: PayloadAction<{ id: string; data?: Partial<CallUser> }>
    ) {
      const { id, data = {} } = action.payload

      const oldUser =
        state.users[id] ??
        Object.values(state.users).find((u) => u.uid === id && !!u.id)

      const checkId = [id, oldUser?.uid, data?.uid].filter(Boolean)

      let nullData =
        checkId.includes(state.users['null']?.uid) && state.users['null']
      let undefinedData =
        checkId.includes(state.users['undefined']?.uid) &&
        state.users['undefined']

      const newUser = createCallUser({
        ...undefinedData,
        ...nullData,
        ...oldUser,
        ...data,
        uid:
          data?.uid ??
          oldUser?.uid ??
          nullData?.uid ??
          undefinedData?.uid ??
          id,
      } as CallUser)

      if (newUser.uid) delete state.users[newUser.uid]
      if (nullData) delete state.users['null']
      if (undefinedData) delete state.users['undefined']

      state.users[newUser.id] = newUser
    },

    // BGM
    setCallBGM(state, action: PayloadAction<CallBGM>) {
      state.bgmSelected = action.payload
    },
    setCallBGMIsPlaying(state, action: PayloadAction<boolean>) {
      state.bgmIsPlaying = action.payload
    },
    setCallBGMTrackID(state, action: PayloadAction<string>) {
      state.bgmTrackId = action.payload
    },

    addCallReaction(
      state,
      action: PayloadAction<{ content: string; userId?: string }>
    ) {
      state.reactions.push({
        id: new Date().getTime(),
        userId: action.payload.userId || state.uid,
        content: action.payload.content,
        startFrom: `${Math.floor(Math.random() * 70) + 20}%`,
      })
    },
    removeCallReaction(state, action: PayloadAction<number>) {
      state.reactions = state.reactions.filter(
        (reaction) => reaction.id !== action.payload
      )
    },
    setRTMToken(state, action: PayloadAction<AgoraState['rtm']>) {
      state.rtm = action.payload
    },
    removeRTMToken(state) {
      state.rtm = undefined
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      callApi.endpoints.getCallDetails.matchFulfilled,
      (state, action) => {
        Object.entries(action.payload.users || {}).forEach(([userId, user]) => {
          if (!state.users[userId]) {
            state.users[userId] = createCallUser(user)
          } else {
            state.users[userId] = {
              ...state.users[userId],
              ...user,
            }
          }
        })
      }
    )
  },
})

const selectAgora = (state) => state.agora as AgoraState

export const selectAgoraNetworkQuality = createDraftSafeSelector(
  selectAgora,
  (state) => state.networkQuality
)

export const selectAgoraBGMSelected = createDraftSafeSelector(
  selectAgora,
  (state) => state.bgmSelected
)

export const selectAgoraBGMIsPlaying = createDraftSafeSelector(
  selectAgora,
  (state) => state.bgmIsPlaying
)

export const selectAgoraBGMTrackID = createDraftSafeSelector(
  selectAgora,
  (state) => state.bgmTrackId
)

export const selectCallReactions = createDraftSafeSelector(
  selectAgora,
  (state) => state.reactions
)

export const selectCallUsers = createDraftSafeSelector(
  selectAgora,
  (state) => state.users
)
export const selectCallUser = createDraftSafeSelector(
  [selectCallUsers, (state: DefaultRootState, id: string) => id],
  (users, id) => users[id]
)
export const selectCallUserLastReaction = createDraftSafeSelector(
  [selectCallReactions, (state: DefaultRootState, id: string) => id],
  (reactions, id) => reactions.filter((r) => r.userId === id).at(-1)
)

export const selectCurrentCallUserID = createDraftSafeSelector(
  selectAgora,
  (state) => state.uid
)

export const selectCurrentCallUser = createDraftSafeSelector(
  [selectCallUsers, selectCurrentCallUserID],
  (users, id) => users[id] ?? Object.values(users).find((u) => u.uid === id)
)

export const selectRTMToken = createDraftSafeSelector(
  selectAgora,
  (state) => state.rtm
)

export const getUserTracks = (userId: string) => {
  if (rtcClient.uid === userId) {
    return getLocalUserTracks()
  } else {
    const remoteUser = rtcClient.remoteUsers.find((u) => u.uid === userId)

    const tracks = {
      audioTrack: remoteUser?.audioTrack,
      videoTrack: remoteUser?.videoTrack,
    }

    return tracks
  }
}

export const getLocalUserTracks = (): {
  audioTrack?: IMicrophoneAudioTrack
  videoTrack?: ICameraVideoTrack
} =>
  rtcClient.localTracks.reduce(
    (prev, lt) => {
      if (
        lt.trackMediaType === 'audio' &&
        typeof (lt as IMicrophoneAudioTrack).setDevice !== 'undefined'
      ) {
        return {
          ...prev,
          audioTrack: lt,
        }
      } else if (lt.trackMediaType === 'video') {
        return {
          ...prev,
          videoTrack: lt,
        }
      } else {
        return prev
      }
    },
    {
      audioTrack: undefined,
      videoTrack: undefined,
    }
  )

export const {
  setupAgora,
  setCallUser,
  setNetworkQuality,
  setCallBGM,
  setCallBGMIsPlaying,
  setCallBGMTrackID,
  addCallReaction,
  removeCallReaction,
  setRTMToken,
  removeRTMToken,
} = agoraSlice.actions

export default agoraSlice.reducer
